<template>
  <main class="voting-page voting-ballot-page">
    <div v-if="loading" class="voting-loading" aria-live="polite">
      <span class="voting-loading__spinner" aria-hidden="true"></span>
      <span>Cargando tu boleta...</span>
    </div>

    <VotingTerminalState
      v-else-if="terminalReason"
      :reason="terminalReason"
      :submitted-at="terminalSubmittedAt"
      :dashboard-route="dashboardRoute"
    />

    <section v-else-if="!manifest" class="voting-error-state">
      <div class="voting-alert voting-alert--error" role="alert">
        <strong>No se pudo cargar la boleta</strong>
        <span>{{ error || "Intenta abrir el proceso nuevamente desde Mis votaciones." }}</span>
      </div>
      <router-link class="voting-button voting-button--secondary" :to="dashboardRoute">
        Volver a mis votaciones
      </router-link>
    </section>

    <template v-else>
      <header class="voting-page__header voting-ballot-page__header">
        <div>
          <router-link class="voting-back-link" :to="dashboardRoute">
            <span aria-hidden="true">&#8592;</span> Mis votaciones
          </router-link>
          <p class="voting-page__eyebrow">
            {{ getVotingTypeLabel(manifest.typeKey) }}<span v-if="manifest.typeVersion"> · v{{ manifest.typeVersion }}</span>
          </p>
          <h1>{{ manifest.title }}</h1>
          <p v-if="manifest.description" class="voting-page__intro">
            {{ manifest.description }}
          </p>
          <p v-if="election && election.closesAt" class="voting-ballot-page__closing">
            <strong>Cierre:</strong> {{ formatDateTime(election.closesAt) }}
          </p>
        </div>
      </header>

      <div v-if="error" class="voting-alert voting-alert--error" role="alert">
        <strong>No se guardó la selección</strong>
        <span>{{ error }}</span>
      </div>

      <section class="voting-contest-list" aria-label="Boleta de votación">
        <article v-for="contest in manifest.contests" :key="contest.key" class="voting-contest-card">
          <VotingContest
            :contest="contest"
            :value="selections[contest.key] || (contest.selectionMode === 'SINGLE' ? '' : [])"
            @input="updateSelection(contest, $event)"
          />
        </article>
      </section>

      <div class="voting-submit-bar">
        <div>
          <strong>{{ saving ? "Guardando selección..." : submitMessage }}</strong>
          <span v-if="saving">Tus cambios se guardarán antes de enviar.</span>
        </div>
        <button
          type="button"
          class="voting-button voting-button--primary"
          :disabled="!isComplete || saving || submitting"
          @click="showConfirm = true"
        >
          Revisar y confirmar
        </button>
      </div>

      <VotingConfirmDialog
        :visible="showConfirm"
        :selection-summary="selectionSummary"
        :submitting="submitting"
        @close="showConfirm = false"
        @confirm="confirmBallot"
      />
    </template>
  </main>
</template>

<script>
import { getVotingErrorMessage, votingService } from "@/api/voting";
import {
  formatVotingDate,
  getVotingTypeLabel,
  normalizeBallotManifest,
} from "@/share/voting";
import VotingConfirmDialog from "./VotingConfirmDialog.vue";
import VotingContest from "./VotingContest.vue";
import VotingTerminalState from "./VotingTerminalState.vue";

export default {
  name: "MemberVotingBallot",
  components: {
    VotingConfirmDialog,
    VotingContest,
    VotingTerminalState,
  },
  data() {
    return {
      manifest: null,
      election: null,
      selections: {},
      loading: true,
      saving: false,
      submitting: false,
      showConfirm: false,
      error: "",
      terminalReason: "",
      terminalSubmittedAt: null,
      saveQueue: Promise.resolve(),
    };
  },
  computed: {
    isV2Route() {
      return this.$route.path.indexOf("/member-v2/") === 0;
    },
    dashboardRoute() {
      return {
        name: this.isV2Route ? "Member V2 Voting" : "Member Voting",
      };
    },
    selectionPayload() {
      return (this.manifest ? this.manifest.contests : []).reduce((payload, contest) => {
        return payload.concat(
          this.selectedKeys(contest).map((optionKey, index) => ({
            contestKey: contest.key,
            optionKey,
            ...(contest.selectionMode === "RANKED" ? { rank: index + 1 } : {}),
          }))
        );
      }, []);
    },
    isComplete() {
      if (!this.manifest) return false;
      return this.manifest.contests.every((contest) => {
        const count = this.selectedKeys(contest).length;
        return (
          contest.allowAbstention ||
          (count >= contest.minSelections && count <= contest.maxSelections)
        );
      });
    },
    selectionSummary() {
      return (this.manifest ? this.manifest.contests : []).map((contest) => ({
        contestKey: contest.key,
        contestTitle: contest.title,
        labels: this.selectedKeys(contest)
          .map((key) => {
            const option = contest.options.find((item) => item.key === key);
            return option && option.label;
          })
          .filter(Boolean),
      }));
    },
    submitMessage() {
      return this.isComplete
        ? "Tu boleta está completa."
        : "Completa los concursos requeridos.";
    },
  },
  created() {
    this.loadBallot();
  },
  methods: {
    getVotingTypeLabel,
    formatDateTime: formatVotingDate,
    async loadBallot() {
      this.loading = true;
      this.error = "";
      try {
        const electionId = this.$route.params.electionId;
        let response;

        try {
          response = await votingService.getBallot(electionId);
        } catch (error) {
          // Permite abrir enlaces directos, como los de correo, sin romper el
          // flujo normal del dashboard que ya creó esta sesión.
          if (!error || !error.response || error.response.status !== 401) {
            throw error;
          }
          await votingService.openProcess(electionId);
          response = await votingService.getBallot(electionId);
        }

        if (response.terminalReason) {
          this.terminalReason = response.terminalReason;
          this.terminalSubmittedAt = response.ballot && response.ballot.submittedAt;
          return;
        }

        this.manifest = normalizeBallotManifest(response.manifest || response);
        this.election = response.election || null;
        this.restoreSelections(response.selections || []);
      } catch (error) {
        const status = error && error.response && error.response.status;
        const message = getVotingErrorMessage(error, "");
        this.terminalReason =
          status === 423
            ? message.toLowerCase().includes("aún no está abierta")
              ? "NOT_OPEN"
              : "ELECTION_CLOSED"
            : status === 410
              ? "TOKEN_EXPIRED"
              : "";
        this.error = getVotingErrorMessage(
          error,
          "No se pudo cargar el manifiesto de la boleta."
        );
      } finally {
        this.loading = false;
      }
    },
    restoreSelections(savedSelections) {
      const restored = {};
      savedSelections.forEach((selection) => {
        const contest = this.manifest.contests.find(
          (item) => item.key === selection.contestKey
        );
        if (!contest) return;
        if (contest.selectionMode === "SINGLE") {
          restored[contest.key] = selection.optionKey;
        } else {
          restored[contest.key] = (restored[contest.key] || []).concat(
            selection.optionKey
          );
        }
      });
      this.selections = restored;
    },
    selectedKeys(contest) {
      const selected = this.selections[contest.key];
      if (contest.selectionMode === "SINGLE") return selected ? [selected] : [];
      return Array.isArray(selected) ? selected : [];
    },
    updateSelection(contest, value) {
      this.$set(this.selections, contest.key, value);
      this.queueSave();
    },
    queueSave() {
      this.error = "";
      this.saveQueue = this.saveQueue
        .catch(() => undefined)
        .then(async () => {
          this.saving = true;
          try {
            await votingService.saveSelections(
              this.$route.params.electionId,
              this.selectionPayload
            );
          } catch (error) {
            this.error = getVotingErrorMessage(
              error,
              "No se pudo guardar tu selección."
            );
          } finally {
            this.saving = false;
          }
        });
    },
    async confirmBallot() {
      if (!this.isComplete || this.saving || this.submitting) return;
      this.submitting = true;
      this.error = "";
      try {
        await this.saveQueue;
        const result = await votingService.submit(
          this.$route.params.electionId,
          this.selectionPayload
        );
        await this.$router.push({
          name: this.isV2Route
            ? "Member V2 Voting Confirmation"
            : "Member Voting Confirmation",
          params: { electionId: this.$route.params.electionId },
          query: {
            participation: JSON.stringify(result.participation || {}),
            closesAt: result.closesAt ? String(result.closesAt) : "",
            resultsPublishAt: result.resultsPublishAt
              ? String(result.resultsPublishAt)
              : "",
            officialAnnouncementChannel: result.officialAnnouncementChannel || "",
            shareUrl: result.shareUrl || "",
            resultsPublished: result.resultsPublished ? "true" : "false",
          },
        });
      } catch (error) {
        const status = error && error.response && error.response.status;
        if (status === 409) this.terminalReason = "ALREADY_SUBMITTED";
        else if (status === 410) this.terminalReason = "TOKEN_EXPIRED";
        else if (status === 423) this.terminalReason = "ELECTION_CLOSED";
        this.error = getVotingErrorMessage(error, "No se pudo enviar la boleta.");
        this.showConfirm = false;
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.voting-page {
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  max-width: 980px;
  margin: 0 auto;
  padding: 32px 28px 64px;
  color: #1b273e;
}

.voting-page__header {
  margin-bottom: 26px;
}

.voting-page__eyebrow {
  margin: 12px 0 0;
  color: #a66f00;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.voting-page h1 {
  margin: 8px 0 0;
  color: #1b273e;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.12;
}

.voting-page__intro {
  max-width: 720px;
  margin: 12px 0 0;
  color: #687386;
  font-size: 15px;
  line-height: 1.65;
}

.voting-back-link {
  color: #1b273e;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
}

.voting-back-link:hover {
  text-decoration: underline;
}

.voting-ballot-page__closing {
  margin: 16px 0 0;
  color: #687386;
  font-size: 13px;
}

.voting-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 14px;
}

.voting-alert--error {
  border: 1px solid #f3c3c0;
  background: #fff2f1;
  color: #8d211b;
}

.voting-alert span {
  flex: 1;
}

.voting-loading {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #687386;
}

.voting-loading__spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(27, 39, 62, 0.25);
  border-top-color: #1b273e;
  border-radius: 50%;
  animation: voting-spin 700ms linear infinite;
}

.voting-error-state {
  max-width: 720px;
  margin: 40px auto;
}

.voting-button {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 999px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

.voting-button--secondary {
  border: 1px solid #cbd3df;
  background: #ffffff;
  color: #1b273e;
}

.voting-button--primary {
  border: 0;
  background: #ffb500;
  color: #1b273e;
}

.voting-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.voting-contest-list {
  display: grid;
  gap: 18px;
}

.voting-contest-card {
  border: 1px solid #dfe4eb;
  border-radius: 20px;
  padding: 24px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(27, 39, 62, 0.06);
}

.voting-submit-bar {
  position: sticky;
  z-index: 2;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 24px;
  border: 1px solid #dfe4eb;
  border-radius: 16px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 30px rgba(27, 39, 62, 0.12);
  backdrop-filter: blur(8px);
}

.voting-submit-bar strong,
.voting-submit-bar span {
  display: block;
}

.voting-submit-bar strong {
  font-size: 14px;
}

.voting-submit-bar span {
  margin-top: 3px;
  color: #687386;
  font-size: 12px;
}

@keyframes voting-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 700px) {
  .voting-page {
    padding: 24px 16px 48px;
  }

  .voting-contest-card {
    padding: 18px 14px;
  }

  .voting-submit-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .voting-submit-bar .voting-button {
    width: 100%;
  }
}
</style>
